// src/pages/admin/MediaDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getSingleMedia, deleteMedia, updateMedia } from '../../utils/api';
import AdminSidebar from '../../components/admin/AdminSidebar';
import Loading from '../../components/common/Loading';
import toast from 'react-hot-toast';
import { 
  FaArrowLeft, 
  FaEdit, 
  FaTrash, 
  FaEye,
  FaDownload,
  FaCopy,
  FaLink,
  FaFileAlt,
  FaImage,
  FaVideo,
  FaMusic,
  FaFilePdf,
  FaInfoCircle,
  FaChartBar,
  FaCogs,
  FaYoutube,
  FaExternalLinkAlt,
  FaCalendarAlt,
  FaTag
} from 'react-icons/fa';

const AdminMediaDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [media, setMedia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({
    title: '',
    description: '',
    category: '',
    is_featured: false
  });

  useEffect(() => {
    loadMedia();
  }, [id]);

// In src/pages/admin/MediaDetail.jsx
const loadMedia = async () => {
    try {
      setLoading(true);
      
      // Try to get single media using the new API function
      const data = await getSingleMedia(id);
      
      console.log('Media detail response:', data);
      
      if (data) {
        setMedia(data);
        setEditData({
          title: data.title || '',
          description: data.description || '',
          category: data.category || '',
          is_featured: data.is_featured || false
        });
      } else {
        toast.error('Media item not found');
        setMedia(null);
      }
    } catch (error) {
      console.error('Error loading media:', error);
      toast.error('Failed to load media details');
      setMedia(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this media file? This action cannot be undone.')) {
      try {
        await deleteMedia(id);
        toast.success('Media deleted successfully');
        navigate('/admin/media');
      } catch (error) {
        console.error('Error deleting media:', error);
        toast.error('Failed to delete media');
      }
    }
  };

  const handleToggleFeatured = async () => {
    try {
      const newFeaturedStatus = !media.is_featured;
      await updateMedia(id, { is_featured: newFeaturedStatus });
      setMedia(prev => ({ ...prev, is_featured: newFeaturedStatus }));
      setEditData(prev => ({ ...prev, is_featured: newFeaturedStatus }));
      toast.success(`Media ${newFeaturedStatus ? 'marked as featured' : 'removed from featured'}`);
    } catch (error) {
      console.error('Error toggling featured status:', error);
      toast.error('Failed to update featured status');
    }
  };

  const handleEditToggle = () => {
    if (editMode) {
      handleSaveEdit();
    } else {
      setEditMode(true);
    }
  };

  const handleSaveEdit = async () => {
    try {
      await updateMedia(id, editData);
      setMedia(prev => ({ ...prev, ...editData }));
      toast.success('Media updated successfully');
      setEditMode(false);
    } catch (error) {
      console.error('Error updating media:', error);
      toast.error('Failed to update media');
    }
  };

  const handleCancelEdit = () => {
    setEditData({
      title: media?.title || '',
      description: media?.description || '',
      category: media?.category || '',
      is_featured: media?.is_featured || false
    });
    setEditMode(false);
  };

  const handleCopyUrl = () => {
    if (!media?.file_url) {
      toast.error('No URL to copy');
      return;
    }
    
    navigator.clipboard.writeText(media.file_url)
      .then(() => toast.success('URL copied to clipboard!'))
      .catch(() => toast.error('Failed to copy URL'));
  };

  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    
    const patterns = [
      /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/i,
      /youtube\.com\/watch\?v=([^"&?\/\s]{11})/i,
      /youtube\.com\/embed\/([^"&?\/\s]{11})/i,
      /youtu\.be\/([^"&?\/\s]{11})/i
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
    
    return null;
  };

  const isYouTubeUrl = (url) => {
    if (!url) return false;
    return getYouTubeVideoId(url) !== null;
  };

  const detectMediaType = (item) => {
    if (!item) return 'unknown';
    
    // Check media_type field first
    if (item.media_type) return item.media_type;
    
    // Check URL
    if (item.file_url) {
      if (isYouTubeUrl(item.file_url)) return 'video';
      
      const urlLower = item.file_url.toLowerCase();
      if (urlLower.match(/\.(jpg|jpeg|png|gif|bmp|webp|svg)$/)) return 'image';
      if (urlLower.match(/\.(mp4|webm|ogg|mov|avi|wmv|flv|mkv)$/)) return 'video';
      if (urlLower.match(/\.(mp3|wav|ogg|flac)$/)) return 'audio';
      if (urlLower.match(/\.(pdf)$/)) return 'document';
    }
    
    return 'other';
  };

  const getMediaTypeIcon = (type) => {
    switch(type) {
      case 'image': return <FaImage className="h-5 w-5" />;
      case 'video': return <FaVideo className="h-5 w-5" />;
      case 'audio': return <FaMusic className="h-5 w-5" />;
      case 'document': return <FaFilePdf className="h-5 w-5" />;
      default: return <FaFileAlt className="h-5 w-5" />;
    }
  };

  const getMediaTypeColor = (type) => {
    switch(type) {
      case 'image': return 'bg-green-100 text-green-800';
      case 'video': return 'bg-red-100 text-red-800';
      case 'audio': return 'bg-purple-100 text-purple-800';
      case 'document': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getFileExtension = (url) => {
    if (!url) return 'N/A';
    
    try {
      // Extract the last part after the last dot
      const urlParts = url.split('?')[0]; // Remove query parameters
      const lastDotIndex = urlParts.lastIndexOf('.');
      
      if (lastDotIndex === -1) return 'N/A';
      
      const extension = urlParts.substring(lastDotIndex + 1).toUpperCase();
      return extension.length > 0 ? extension : 'N/A';
    } catch (error) {
      return 'N/A';
    }
  };

  const renderMediaPreview = () => {
    if (!media) return null;
    
    const mediaType = detectMediaType(media);
    
    if (mediaType === 'video' && isYouTubeUrl(media.file_url)) {
      const videoId = getYouTubeVideoId(media.file_url);
      return (
        <div className="relative w-full aspect-video">
          <iframe
            src={`https://www.youtube.com/embed/${videoId}`}
            title={media.title}
            className="w-full h-full rounded-lg"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
          <div className="absolute top-4 left-4 bg-red-600 text-white px-3 py-1 rounded-full text-sm flex items-center">
            <FaYoutube className="h-4 w-4 mr-2" />
            YouTube
          </div>
        </div>
      );
    } else if (mediaType === 'video' && media.file_url) {
      return (
        <div className="relative w-full aspect-video bg-gray-900 rounded-lg overflow-hidden">
          <video 
            src={media.file_url} 
            className="w-full h-full object-contain"
            controls
            autoPlay={false}
          >
            Your browser does not support the video tag.
          </video>
        </div>
      );
    } else if (mediaType === 'image' && media.file_url) {
      return (
        <div className="relative">
          <img 
            src={media.file_url} 
            alt={media.title}
            className="w-full h-auto max-h-[500px] object-contain rounded-lg shadow-lg"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = '/default-media.jpg';
            }}
          />
        </div>
      );
    } else if (mediaType === 'audio' && media.file_url) {
      return (
        <div className="bg-gray-100 rounded-lg p-8">
          <div className="flex items-center justify-center mb-6">
            <FaMusic className="h-20 w-20 text-purple-500" />
          </div>
          <audio 
            src={media.file_url} 
            controls
            className="w-full"
          >
            Your browser does not support the audio element.
          </audio>
        </div>
      );
    } else if (mediaType === 'document' && media.file_url) {
      return (
        <div className="bg-gray-100 rounded-lg p-8">
          <div className="flex items-center justify-center mb-6">
            <FaFilePdf className="h-20 w-20 text-blue-500" />
          </div>
          <div className="text-center">
            <p className="text-lg font-medium text-gray-800 mb-2">{media.title}</p>
            <p className="text-gray-600 mb-4">Document File</p>
            <a 
              href={media.file_url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <FaExternalLinkAlt className="h-4 w-4 mr-2" />
              Open Document
            </a>
          </div>
        </div>
      );
    } else {
      return (
        <div className="bg-gray-100 rounded-lg p-8">
          <div className="flex items-center justify-center mb-6">
            <FaFileAlt className="h-20 w-20 text-gray-500" />
          </div>
          <div className="text-center">
            <p className="text-lg font-medium text-gray-800 mb-2">{media?.title || 'Unknown Media'}</p>
            <p className="text-gray-600 mb-4">Media File</p>
            {media?.file_url ? (
              <a 
                href={media.file_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <FaDownload className="h-4 w-4 mr-2" />
                Download File
              </a>
            ) : (
              <p className="text-red-600">No file URL available</p>
            )}
          </div>
        </div>
      );
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const getFileSize = () => {
    // This would typically come from the backend
    // For now, we'll show a placeholder
    return 'Unknown size';
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-gray-100">
        <AdminSidebar />
        <div className="flex-1 p-8">
          <Loading fullScreen message="Loading media details..." />
        </div>
      </div>
    );
  }

  if (!media) {
    return (
      <div className="flex min-h-screen bg-gray-100">
        <AdminSidebar />
        <div className="flex-1 p-8 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Media Not Found</h2>
            <Link to="/admin/media" className="text-blue-600 hover:text-blue-800 inline-flex items-center">
              <FaArrowLeft className="h-4 w-4 mr-2" />
              Back to Media Library
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const mediaType = detectMediaType(media);

  return (
    <div className="flex min-h-screen bg-gray-100">
      <AdminSidebar />
      <div className="flex-1 p-8">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Media Details</h1>
              <p className="text-gray-600 mt-2">
                View and manage details for: <span className="font-semibold text-gray-800">{media?.title || 'Untitled Media'}</span>
              </p>
            </div>
            <div className="flex space-x-3">
              <Link
                to="/admin/media"
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <FaArrowLeft className="h-4 w-4 mr-2" />
                Back to Media
              </Link>
              <button
                onClick={handleEditToggle}
                className={`inline-flex items-center px-4 py-2 rounded-lg transition-colors ${
                  editMode 
                    ? 'bg-green-600 text-white hover:bg-green-700' 
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                <FaEdit className="h-4 w-4 mr-2" />
                {editMode ? 'Save Changes' : 'Edit Media'}
              </button>
              {editMode && (
                <button
                  onClick={handleCancelEdit}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel Edit
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-xl shadow-lg">
          {/* Media Preview Card */}
          <div className="p-6 border-b">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Media Preview */}
              <div className="lg:w-2/3">
                <div className="mb-4 flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className={`px-3 py-1 rounded-full text-sm font-medium flex items-center ${getMediaTypeColor(mediaType)}`}>
                      {getMediaTypeIcon(mediaType)}
                      <span className="ml-2">{mediaType.toUpperCase()}</span>
                    </div>
                    {media?.is_featured && (
                      <span className="px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                        Featured
                      </span>
                    )}
                  </div>
                  
                  <button
                    onClick={handleToggleFeatured}
                    className={`px-4 py-2 rounded-lg text-sm font-medium ${
                      media?.is_featured
                        ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        : 'bg-yellow-50 text-yellow-600 hover:bg-yellow-100'
                    }`}
                  >
                    {media?.is_featured ? 'Remove Featured' : 'Mark as Featured'}
                  </button>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  {renderMediaPreview()}
                </div>

                {/* File URL */}
                {media?.file_url && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">File URL</label>
                    <div className="flex">
                      <input
                        type="text"
                        value={media.file_url}
                        readOnly
                        className="flex-1 p-2 border border-gray-300 rounded-l-lg bg-gray-50 text-gray-700 truncate"
                      />
                      <button
                        onClick={handleCopyUrl}
                        className="px-4 py-2 bg-gray-200 text-gray-700 hover:bg-gray-300 border border-l-0 border-gray-300 rounded-r-lg transition-colors"
                        title="Copy URL"
                      >
                        <FaCopy className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Media Details */}
              <div className="lg:w-1/3">
                <div className="space-y-6">
                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title
                    </label>
                    {editMode ? (
                      <input
                        type="text"
                        value={editData.title}
                        onChange={(e) => setEditData({...editData, title: e.target.value})}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    ) : (
                      <h2 className="text-xl font-bold text-gray-800">{media?.title || 'Untitled Media'}</h2>
                    )}
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <FaFileAlt className="h-4 w-4 mr-2" />
                      Description
                    </label>
                    {editMode ? (
                      <textarea
                        value={editData.description}
                        onChange={(e) => setEditData({...editData, description: e.target.value})}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        rows="4"
                      />
                    ) : (
                      <div className="prose max-w-none">
                        <p className="text-gray-600 whitespace-pre-line">
                          {media?.description || 'No description provided.'}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Category */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <FaTag className="h-4 w-4 mr-2" />
                      Category
                    </label>
                    {editMode ? (
                      <input
                        type="text"
                        value={editData.category}
                        onChange={(e) => setEditData({...editData, category: e.target.value})}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., Promotional, Tutorials, Documents"
                      />
                    ) : (
                      <div className="font-medium text-gray-800">
                        {media?.category || 'Uncategorized'}
                      </div>
                    )}
                  </div>

                  {/* File Info */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">File Information</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">File Type</span>
                        <span className="text-sm font-medium text-gray-800">{mediaType}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">File Size</span>
                        <span className="text-sm font-medium text-gray-800">{getFileSize()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Format</span>
                        <span className="text-sm font-medium text-gray-800">
                          {media?.file_url ? getFileExtension(media.file_url) : 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs Navigation */}
          <div className="border-b">
            <div className="flex space-x-1 px-6">
              {['overview', 'details', 'analytics'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
                    activeTab === tab
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab === 'overview' && <FaInfoCircle className="inline mr-2 h-4 w-4" />}
                  {tab === 'details' && <FaCogs className="inline mr-2 h-4 w-4" />}
                  {tab === 'analytics' && <FaChartBar className="inline mr-2 h-4 w-4" />}
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* System Information */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                    <FaInfoCircle className="h-5 w-5 mr-2" />
                    System Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-gray-500">Media ID</div>
                      <div className="font-medium text-gray-800 truncate">{media?.id || 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 flex items-center">
                        <FaCalendarAlt className="h-4 w-4 mr-2" />
                        Created
                      </div>
                      <div className="font-medium text-gray-800">
                        {media?.created_at ? formatDate(media.created_at) : 'N/A'}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Updated</div>
                      <div className="font-medium text-gray-800">
                        {media?.updated_at ? formatDate(media.updated_at) : 'N/A'}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Status</div>
                      <div className="font-medium text-gray-800">
                        <span className={`inline-flex items-center ${media?.is_featured ? 'text-yellow-600' : 'text-gray-600'}`}>
                          <FaTag className="h-3 w-3 mr-1" />
                          {media?.is_featured ? 'Featured' : 'Normal'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="bg-white border rounded-lg p-6">
                  <h3 className="text-lg font-bold text-gray-800 mb-4">Quick Actions</h3>
                  <div className="flex flex-wrap gap-3">
                    {media?.file_url && (
                      <>
                        <a 
                          href={media.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          <FaEye className="h-4 w-4 mr-2" />
                          View Full Size
                        </a>
                        <a 
                          href={media.file_url}
                          download
                          className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                          <FaDownload className="h-4 w-4 mr-2" />
                          Download File
                        </a>
                        <button
                          onClick={handleCopyUrl}
                          className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                          <FaLink className="h-4 w-4 mr-2" />
                          Copy URL
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => navigate(`/admin/media?edit=${media?.id}`)}
                      className="inline-flex items-center px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                    >
                      <FaEdit className="h-4 w-4 mr-2" />
                      Edit File
                    </button>
                  </div>
                </div>

                {/* Usage Statistics */}
                <div className="bg-white border rounded-lg p-6">
                  <h3 className="text-lg font-bold text-gray-800 mb-4">Usage Statistics</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <div className="text-2xl font-bold text-blue-600">0</div>
                      <div className="text-sm text-gray-600">Views</div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <div className="text-2xl font-bold text-green-600">0</div>
                      <div className="text-sm text-gray-600">Downloads</div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <div className="text-2xl font-bold text-purple-600">0</div>
                      <div className="text-sm text-gray-600">Shares</div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <div className="text-2xl font-bold text-yellow-600">N/A</div>
                      <div className="text-sm text-gray-600">File Size</div>
                    </div>
                  </div>
                </div>

                {/* Danger Zone */}
                <div className="border border-red-200 rounded-lg p-6 bg-red-50">
                  <h3 className="text-lg font-bold text-red-800 mb-2">Danger Zone</h3>
                  <p className="text-red-600 mb-4">
                    Deleting this media will remove it from the server and any content using it.
                    This action cannot be undone.
                  </p>
                  <button
                    onClick={handleDelete}
                    className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <FaTrash className="h-4 w-4 mr-2" />
                    Delete Media
                  </button>
                </div>
              </div>
            )}

            {/* Details Tab */}
            {activeTab === 'details' && (
              <div className="space-y-6">
                {/* Raw Media Data */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-bold text-gray-800 mb-4">Raw Media Data</h3>
                  <div className="bg-white border rounded-lg overflow-hidden">
                    <div className="p-4 bg-gray-800 text-white text-sm font-mono">
                      JSON Response
                    </div>
                    <pre className="p-4 text-sm text-gray-700 overflow-x-auto max-h-[400px]">
                      {JSON.stringify(media, null, 2)}
                    </pre>
                  </div>
                </div>

                {/* API Information */}
                <div className="bg-white border rounded-lg p-6">
                  <h3 className="text-lg font-bold text-gray-800 mb-4">API Information</h3>
                  <div className="space-y-4">
                    <div>
                      <div className="text-sm text-gray-500 mb-1">GET Endpoint</div>
                      <code className="bg-gray-100 px-3 py-2 rounded text-sm font-mono block">
                        GET /api/media/{media?.id || ':id'}
                      </code>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-1">UPDATE Endpoint</div>
                      <code className="bg-gray-100 px-3 py-2 rounded text-sm font-mono block">
                        PUT /api/media/{media?.id || ':id'}
                      </code>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-1">DELETE Endpoint</div>
                      <code className="bg-gray-100 px-3 py-2 rounded text-sm font-mono block">
                        DELETE /api/media/{media?.id || ':id'}
                      </code>
                    </div>
                  </div>
                </div>

                {/* Embed Code */}
                <div className="bg-white border rounded-lg p-6">
                  <h3 className="text-lg font-bold text-gray-800 mb-4">Embed Code</h3>
                  <div className="space-y-4">
                    {media?.file_url && (
                      <>
                        <div>
                          <div className="text-sm text-gray-500 mb-1">Direct Link</div>
                          <code className="bg-gray-100 px-3 py-2 rounded text-sm font-mono block break-all">
                            {media.file_url}
                          </code>
                        </div>
                        {mediaType === 'image' && (
                          <div>
                            <div className="text-sm text-gray-500 mb-1">HTML Image Tag</div>
                            <code className="bg-gray-100 px-3 py-2 rounded text-sm font-mono block">
                              {`<img src="${media.file_url}" alt="${media.title || 'Image'}" />`}
                            </code>
                          </div>
                        )}
                        {mediaType === 'video' && isYouTubeUrl(media.file_url) && (
                          <div>
                            <div className="text-sm text-gray-500 mb-1">YouTube Embed Code</div>
                            <code className="bg-gray-100 px-3 py-2 rounded text-sm font-mono block">
                              {`<iframe width="560" height="315" src="https://www.youtube.com/embed/${getYouTubeVideoId(media.file_url)}" frameborder="0" allowfullscreen></iframe>`}
                            </code>
                          </div>
                        )}
                      </>
                    )}
                    {!media?.file_url && (
                      <p className="text-gray-500 italic">No file URL available for embedding.</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Analytics Tab */}
            {activeTab === 'analytics' && (
              <div className="text-center py-12">
                <div className="inline-block p-8 bg-gray-50 rounded-lg">
                  <FaChartBar className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-bold text-gray-800 mb-2">Analytics Coming Soon</h3>
                  <p className="text-gray-600">
                    Media analytics, including views, downloads, and usage statistics,
                    are currently in development.
                  </p>
                  <div className="mt-6 grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-blue-600">0</div>
                      <div className="text-sm text-gray-600">Total Views</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-green-600">0</div>
                      <div className="text-sm text-gray-600">Downloads</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-purple-600">0</div>
                      <div className="text-sm text-gray-600">Referrals</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminMediaDetail;